import { useRef, MouseEvent } from 'react';
import { motion, useMotionValue, useSpring, useTransform } from 'framer-motion';
import { cn } from '@/components/ui/utils';
import { useTheme } from '../../contexts/ThemeContext';

interface SpotlightCardProps extends React.HTMLAttributes<HTMLDivElement> {
    children: React.ReactNode;
    className?: string;
    spotlightColor?: string;
    /** Enable physics-like tilt effect on hover */
    enableTilt?: boolean;
    /** Maximum tilt angle in degrees */
    maxTilt?: number;
}

export default function SpotlightCard({
    children,
    className = '',
    spotlightColor,
    enableTilt = true,
    maxTilt = 8,
    ...props
}: SpotlightCardProps) {
    const { theme } = useTheme();
    const divRef = useRef<HTMLDivElement>(null);

    // Motion values for spotlight position
    const spotlightX = useMotionValue(0);
    const spotlightY = useMotionValue(0);
    const spotlightOpacity = useMotionValue(0);

    // Motion values for tilt (raw mouse position normalized -1 to 1)
    const mouseX = useMotionValue(0);
    const mouseY = useMotionValue(0);

    // Spring physics for smooth, momentum-based animations
    const springConfig = { damping: 20, stiffness: 300, mass: 0.5 };

    const springX = useSpring(mouseX, springConfig);
    const springY = useSpring(mouseY, springConfig);

    // Transform spring values to rotation angles
    const rotateX = useTransform(springY, [-1, 1], [maxTilt, -maxTilt]);
    const rotateY = useTransform(springX, [-1, 1], [-maxTilt, maxTilt]);

    // Subtle scale on hover
    const scale = useSpring(1, { damping: 20, stiffness: 400 });

    const isDark = theme === 'dark';
    const defaultSpotlightColor = isDark ? 'rgba(74, 124, 255, 0.08)' : 'rgba(0, 71, 171, 0.06)';

    const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
        if (!divRef.current) return;

        const rect = divRef.current.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // Spotlight follows cursor
        spotlightX.set(x);
        spotlightY.set(y);

        // Normalize position to -1 to 1 for tilt calculation
        if (enableTilt) {
            const normalizedX = (x / rect.width) * 2 - 1;
            const normalizedY = (y / rect.height) * 2 - 1;
            mouseX.set(normalizedX);
            mouseY.set(normalizedY);
        }
    };

    const handleMouseEnter = () => {
        spotlightOpacity.set(1);
        scale.set(1.02);
    };

    const handleMouseLeave = () => {
        spotlightOpacity.set(0);
        scale.set(1);
        // Reset tilt with spring animation
        mouseX.set(0);
        mouseY.set(0);
    };

    // Use motion values directly in style
    const spotlightStyle = {
        opacity: spotlightOpacity,
        background: useTransform(
            [spotlightX, spotlightY],
            ([x, y]) => `radial-gradient(500px circle at ${x}px ${y}px, ${spotlightColor || defaultSpotlightColor}, transparent 50%)`
        ),
    };

    return (
        <motion.div
            ref={divRef}
            onMouseMove={handleMouseMove}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            className={cn(
                `relative overflow-hidden rounded-3xl backdrop-blur-sm transition-colors duration-300 hover:border-[var(--color-uf-blue)]/20 ${isDark
                    ? 'border border-white/[0.06] bg-white/[0.02]'
                    : 'border border-black/[0.06] bg-black/[0.02]'
                }`,
                className
            )}
            style={{
                rotateX: enableTilt ? rotateX : 0,
                rotateY: enableTilt ? rotateY : 0,
                scale,
                transformStyle: 'preserve-3d',
                perspective: 1000,
            }}
            {...props}
        >
            <motion.div
                className="pointer-events-none absolute -inset-px transition duration-500"
                style={spotlightStyle}
            />
            <div className="relative h-full" style={{ transform: 'translateZ(0)' }}>
                {children}
            </div>
        </motion.div>
    );
}
